import ReportConfigLockResponse from '../../../types/report/ReportConfigLockResponse';
import reportConfigLockResult from '../../../infrastructure/constants/reportConfigLockResult';
import Statement from '../../../infrastructure/types/Functions/Statement';
import typeHelper from '../../../infrastructure/helpers/common/typeHelper';
import SelectOption from '../../../infrastructure/types/SelectOption';
import { SelectSearchOption } from 'react-select-search';
import ReportConfig from '../../../types/report/ReportConfig';
import ComponentType from '../../../types/report/ComponentType';
import Assignment from '../../../types/functions/Assignment/Assignment';
import Condition from '../../../types/functions/Condition/Condition';
import Aggregation from '../../../types/report/Aggregation';
import ComponentNumberAndAssignment from '../../../types/report/ComponentNumberAndAssignment';
import DataSourceField from '../../../types/report/DataSourceField';
import Dictionary from '../../../types/report/Dictionary';
import DictionaryField from '../../../types/report/DictionaryField';
import ReportCase from '../../../types/report/ReportCase';
import ReportCaseField from '../../../types/report/ReportCaseField';
import Selector from '../../../types/report/Selector';
import CacheKey from '../../../types/report/CacheKey';
import reorderHelper from '../../../infrastructure/helpers/common/reorderHelper';
import Operand from '../../../types/functions/Operand/Operand';
import NameAndNumber from '../../../types/report/NameAndNumber';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import locationType from '../../../types/functions/Location/LocationType';

const allowEdit = (response: ReportConfigLockResponse): boolean => {

    return response.result === reportConfigLockResult.success;
};

const getLockedMessage = (response: ReportConfigLockResponse): string | null => {

    if (response.result === reportConfigLockResult.lockedByAnotherUser) {

        return `Config is locked. User ${response.lockedBy} is currently editing this config`;
    }

    return null;
};

const isResetRequired = (number: number | null, statements: Statement[]): boolean => {

    if (!typeHelper.isNumber(number)) {

        return false;
    }

    return !statements.some(c => c.number === number);
};

const createSelectSearchOption = (field: SelectOption): SelectSearchOption => {
    return {
        name: field.text,
        value: field.value.toString(),
    };
};

const setValidationConfigComponentsProperties = (reportConfig: ReportConfig, id: number, isUsedByCurrentUser: boolean, componentType: ComponentType): void => {

    reportConfig.variables.forEach((variable) => {
        if (variable.id === id && componentType === ComponentType.Variable && isUsedByCurrentUser) {
            variable.isReadOnly = false;
            variable.isUsedByCurrentUser = true;
        }
        else {
            variable.isReadOnly = true;
        }
        variable.isLocked = true;
    });
    reportConfig.lookups.forEach((lookup) => {
        if (lookup.id === id && componentType === ComponentType.Lookup && isUsedByCurrentUser) {
            lookup.isReadOnly = false;
            lookup.isUsedByCurrentUser = true;
        }
        else {
            lookup.isReadOnly = true;
        }
        lookup.isLocked = true;
    });
    reportConfig.isinSelectors.forEach((isinSelector) => {
        if (isinSelector.id === id && componentType === ComponentType.IsinSelector && isUsedByCurrentUser) {
            isinSelector.isReadOnly = false;
            isinSelector.isUsedByCurrentUser = true;
        }
        else {
            isinSelector.isReadOnly = true;
        }
        isinSelector.isLocked = true;
    });
    reportConfig.leiSelectors.forEach((leiSelector) => {
        if (leiSelector.id === id && componentType === ComponentType.LeiSelector && isUsedByCurrentUser) {
            leiSelector.isReadOnly = false;
            leiSelector.isUsedByCurrentUser = true;
        }
        else {
            leiSelector.isReadOnly = true;
        }
        leiSelector.isLocked = true;
    });
    reportConfig.exchangeRatesSelectors.forEach((exchangeRatesSelector) => {
        if (exchangeRatesSelector.id === id && componentType === ComponentType.ExchangeRatesSelector && isUsedByCurrentUser) {
            exchangeRatesSelector.isReadOnly = false;
            exchangeRatesSelector.isUsedByCurrentUser = true;
        }
        else {
            exchangeRatesSelector.isReadOnly = true;
        }
        exchangeRatesSelector.isLocked = true;
    });
    reportConfig.annaDsbUpiEnrichmentSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.AnnaDsbUpiEnrichmentSelector && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });
    reportConfig.annaDsbUpiSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.AnnaDsbUpiSelector && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });

    reportConfig.lseSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.LseSelector && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });

    reportConfig.symbolSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.SymbolSelector && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });

    reportConfig.firdsFcaInstrumentSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.FirdsFcaInstrumentSelectors && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });
    reportConfig.firdsEsmaInstrumentSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.FirdsEsmaInstrumentSelectors && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });
    reportConfig.validations.forEach((validation) => {
        if (validation.id === id && componentType === ComponentType.AccuracyValidation && isUsedByCurrentUser) {
            validation.isReadOnly = false;
            validation.isUsedByCurrentUser = true;
        }
        else {
            validation.isReadOnly = true;
        }
        validation.isLocked = true;
    });
    reportConfig.fcaRegulatedEntitiesSelectors.forEach((selector) => {
        if (selector.id === id && componentType === ComponentType.FCARegulatedEntitiesSelector && isUsedByCurrentUser) {
            selector.isReadOnly = false;
            selector.isUsedByCurrentUser = true;
        }
        else {
            selector.isReadOnly = true;
        }
        selector.isLocked = true;
    });
}

const setSelectorAssignmentByKey = (assignment: Assignment, selectors: Selector[], number: number, key: string): void => {

    let item = selectors.find(x => x.number === number);

    if (item?.keys?.length) {
        let selKey = item.keys.find(x => x.name === key) as CacheKey;

        selKey.assignment = assignment;
    }
}

const setCondition = (condition: Condition, statements: ComponentNumberAndAssignment[], number: number): void => {
    let statement = statements.find(x => x.number === number) as Statement;

    statement.condition = condition;
}

const setAssignment = (assignment: Assignment, items: ComponentNumberAndAssignment[], number: number): void => {
    let item = items.find(x => x.number === number) as ComponentNumberAndAssignment;

    item.assignment = assignment;
}

const setReporCasetFieldAssignment = (assignment: Assignment, reportCases: ReportCase[], caseNumber: number, fieldId: number): void => {
    let fields = reportCases.find(x => x.number === caseNumber)?.fields as ReportCaseField[];

    let field = fields.find(x => x.fieldId === fieldId) as ReportCaseField;

    field.assignment = assignment;
}

const setAggregationFieldAssignment = (assignment: Assignment, aggregations: Aggregation[], number: number, fieldId: number, subType: string | undefined): void => {

    let aggr = aggregations.find(x => x.number === number);

    if (subType === locationType.statement.aggregatedRawField) {
        let field = aggr?.fields.find(x => x.fieldId === fieldId) as DataSourceField;

        field.assignment = assignment;
    }

    if (subType === locationType.statement.aggregatedVariable) {
        let field = aggr?.fields.find(x => x.variableNumber === fieldId) as DataSourceField;

        field.assignment = assignment;
    }
}


const setDictionaryAssignment = (assignment: Assignment, dictionaries: Dictionary[], number: number, fieldNumber: number): void => {
    let dict = dictionaries.find(x => x.number === number) as Dictionary;

    let field = dict.fields.find(x => x.number === fieldNumber) as DictionaryField;

    field.assignment = assignment;
}

const updateFunctionReferencesOnDelete = (functions: UserDefinedFunction[], reportConfig: ReportConfig, deletedFunctionnumber: number) => {

    let functionsToUse = reportConfig.userDefinedFunctions.filter(x => x.number >= deletedFunctionnumber);

    let changeMap = new Map<number, number>();

    functionsToUse.forEach((f) => {

        let oldNumber = f.number + 1;

        changeMap.set(oldNumber, f.number);
    });

    reorderHelper.changeReferences(reportConfig,
        functions, (operand: Operand, oldAndNewNumbers: Map<number, number>, componentType?: ComponentType | undefined) => {
            if (componentType !== ComponentType.AccuracyValidation) {
                if (operand.functionNumber && changeMap.has(operand.functionNumber)) {
                    operand.functionNumber = changeMap.get(operand.functionNumber) as number;
                }
            }
        }
    );
}

const resetNumbers = (collection: NameAndNumber[]) => {
    for (let index = 0; index < collection.length; index++) {
        const element = collection[index];

        element.number = index + 1;
    }
}

export {
    allowEdit,
    getLockedMessage,
    isResetRequired,
    createSelectSearchOption,
    setValidationConfigComponentsProperties,
    setDictionaryAssignment,
    setSelectorAssignmentByKey,
    setAggregationFieldAssignment,
    setAssignment,
    setCondition,
    setReporCasetFieldAssignment,
    updateFunctionReferencesOnDelete,
    resetNumbers
};